// Smart URL resolution for development proxy and production
const getBaseUrl = () => {
  // In development with proxy configuration, use empty string for relative URLs
  if (process.env.NODE_ENV === 'development') {
    return ''
  }

  // In production, use the configured backend URL
  const configuredUrl = process.env.VUE_APP_BACKEND_BASE_URL

  // If explicitly set to empty string, return empty for relative URLs (proxy mode)
  if (configuredUrl === '') return ''

  // Otherwise use configured URL or default fallback
  return configuredUrl || 'http://localhost:3005'
}

// Check if we're in development mode without backend
const isDevelopmentWithoutBackend = () => {
  return process.env.NODE_ENV === 'development' && (
    !process.env.VUE_APP_BACKEND_BASE_URL ||
    process.env.VUE_APP_BACKEND_BASE_URL === ''
  )
}

// Get authorization headers with JWT token
const getAuthHeaders = () => {
  const token = window.sessionStorage.getItem('ACCESS_TOKEN')
  return {
    'content-type': 'application/json',
    'X-Client-Type': 'web',
    ...(token && { Authorization: `Bearer ${token}` })
  }
}

// Merge extra headers on top of the default authorization headers
const getRequestHeaders = (extraHeaders = {}) => {
  return {
    ...getAuthHeaders(),
    ...extraHeaders
  }
}

// Create a simple request deduplication mechanism to prevent duplicate requests
// IMPORTANT: We cache the FULL request pipeline (fetch + response parsing) to avoid
// the "body stream already read" error. Each deduplicated caller gets the same parsed result.
const activeRequests = new Map()

// Request wrapper with deduplication that caches the complete request-response cycle
// Returns a Promise that resolves to the parsed JSON response, not the raw Response object
const makeRequest = async (url, options, responseHandler) => {
  const requestKey = `${options.method || 'GET'}:${url}`

  // If the same request is already in flight, return the existing promise
  // Since we cache the parsed result, multiple callers can safely use the same data
  if (activeRequests.has(requestKey)) {
    return activeRequests.get(requestKey)
  }

  // Create the complete request pipeline: fetch -> handle response -> parse JSON
  // This ensures that only ONE Response body is ever read, and all callers share the parsed result
  const requestPromise = (async () => {
    try {
      const response = await fetch(url, options)
      // Add method info for debugging
      Object.defineProperty(response, 'method', { value: options.method || 'GET', writable: false })
      // Handle the response and parse JSON
      return await responseHandler(response)
    } finally {
      // Clean up after request completes (success or failure)
      activeRequests.delete(requestKey)
    }
  })()

  activeRequests.set(requestKey, requestPromise)
  return requestPromise
}

// Handle API responses and errors consistently
const handleApiResponse = async (response) => {
  const url = response.url || 'unknown'

  if (response.status === 401) {
    // Extract backend error message to check for specific authentication exceptions
    let backendError = null
    try {
      const responseBody = await response.clone().json()
      if (responseBody && responseBody.error) {
        backendError = responseBody.error
      }
    } catch (e) {
      // Ignore JSON parsing errors
    }

    // Check for authentication flow exceptions that need special handling
    // These errors should be passed through to the login page for proper handling
    const authFlowExceptions = [
      'UserNotConfirmedException',
      'PasswordResetRequiredException',
      'UserNotFoundException',
      'NotAuthorizedException',
      'email is not verified'
    ]

    const isAuthFlowException = backendError && authFlowExceptions.some(
      exception => backendError.includes(exception)
    )

    // For auth flow exceptions during login, pass through the error without clearing session
    const currentPath = window.location.pathname
    const isLoginPath = currentPath.startsWith('/auth/login')

    if (isAuthFlowException && isLoginPath) {
      // Return the error as a result object so Login page can handle it
      return { error: backendError, success: false }
    }

    // For normal 401 errors (token expired, etc.), clear session
    window.sessionStorage.clear()

    // Only redirect if we're not in the middle of authentication flow
    const authPaths = ['/auth/login', '/auth/register', '/auth/mfa', '/auth/setup', '/auth/confirm-signup']
    const isAuthPath = authPaths.some(path => currentPath.startsWith(path))

    if (!isAuthPath) {
      // Show a user-friendly message and redirect
      setTimeout(() => {
        window.location.href = '/auth/login?redirectTo=' + encodeURIComponent(currentPath)
      }, 100)
    }

    throw new Error(backendError || 'Authentication required')
  }

  if (response.status === 403) {
    // Auth v2 can return a specific, actionable error with a 403. Preserve it
    // so the sign-in UI displays the message supplied by the backend.
    let backendError = ''
    try {
      const responseBody = await response.clone().json()
      backendError = responseBody?.error || responseBody?.message || ''
    } catch (_) {}
    throw new Error(backendError || 'Insufficient permissions')
  }

  if (!response.ok) {
    let errorMessage = `API Error: ${response.status} ${response.statusText}`
    let responseBody = null
    let backendError = ''

    try {
      // Try to get additional error information from response body
      responseBody = await response.clone().json()
      const responseError = responseBody?.error || responseBody?.message
      backendError = typeof responseError === 'string' ? responseError : responseError?.message || ''
      if (backendError) {
        errorMessage += ` - ${backendError}`
      }
    } catch (e) {
      // If response body isn't JSON, try to get text
      try {
        const textBody = await response.clone().text()
        if (textBody && textBody.trim()) {
          errorMessage += ` - ${textBody.substring(0, 200)}`
        }
      } catch (textError) {
        // Ignore text parsing errors
      }
    }

    // For 500 errors, provide more user-friendly messaging
    if (response.status >= 500) {
      if (backendError) {
        errorMessage = backendError
      } else {
        errorMessage = `Server Error (${response.status}): The server encountered an issue processing your request`
        if (url.includes('request') || url.includes('delivery')) {
          errorMessage += '. This may be a temporary issue with request processing.'
        }
      }
    }

    throw new Error(errorMessage)
  }

  return response.json()
}

export async function fetchApiData (path) {
  // In development without backend, return mock data for key endpoints
  if (isDevelopmentWithoutBackend()) {
    console.warn(`[DEV MODE] Mocking API call to ${path}`)

    // Mock responses for authorization endpoints
    if (path === 'api/user/me' || path === 'api/auth/roles/me') {
      return {
        id: 'dev-user',
        role: 'bits_user',
        organization_id: null,
        email: 'dev@example.com'
      }
    }

    if (path.startsWith('api/auth/permissions')) {
      return { hasPermission: true }
    }

    if (path.includes('events')) {
      return { events: [], total: 0 }
    }

    // For other endpoints, return empty data to prevent crashes
    return {}
  }

  const baseUrl = getBaseUrl()
  const url = baseUrl ? `${baseUrl}/${path}` : `/${path}`

  try {
    // Use makeRequest with the response handler to ensure proper deduplication
    // The handler is passed to makeRequest so the parsed result can be cached
    return await makeRequest(
      url,
      {
        method: 'GET',
        headers: getAuthHeaders()
      },
      handleApiResponse
    )
  } catch (error) {
    // Add additional context to the error
    error.apiPath = path
    error.apiUrl = url
    error.apiMethod = 'GET'
    throw error
  }
}

export async function postApiData (path, data, extraHeaders = {}) {
  // In development without backend, return mock success responses
  if (isDevelopmentWithoutBackend()) {
    console.warn(`[DEV MODE] Mocking API POST to ${path}`)

    // Mock responses for common POST endpoints
    if (path.includes('auth/') || path.includes('key')) {
      return { success: true, message: 'Mock success' }
    }

    // Return success for other POST requests
    return { success: true, data: { id: 'mock-id' } }
  }

  const baseUrl = getBaseUrl()
  const url = baseUrl ? `${baseUrl}/${path}` : `/${path}`

  try {
    // Use makeRequest with the response handler for proper deduplication
    return await makeRequest(
      url,
      {
        method: 'POST',
        headers: getRequestHeaders(extraHeaders),
        body: JSON.stringify(data)
      },
      handleApiResponse
    )
  } catch (error) {
    error.apiPath = path
    error.apiUrl = url
    error.apiMethod = 'POST'
    throw error
  }
}

export async function putApiData (path, data = null, extraHeaders = {}) {
  const baseUrl = getBaseUrl()
  const url = baseUrl ? `${baseUrl}/${path}` : `/${path}`

  try {
    // Use makeRequest with the response handler for proper deduplication
    return await makeRequest(
      url,
      {
        method: 'PUT',
        headers: getRequestHeaders(extraHeaders),
        ...(data && { body: JSON.stringify(data) })
      },
      handleApiResponse
    )
  } catch (error) {
    error.apiPath = path
    error.apiUrl = url
    error.apiMethod = 'PUT'
    throw error
  }
}

export async function deleteApiData (path) {
  const baseUrl = getBaseUrl()
  const url = baseUrl ? `${baseUrl}/${path}` : `/${path}`

  try {
    // Use makeRequest with the response handler for proper deduplication
    return await makeRequest(
      url,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      },
      handleApiResponse
    )
  } catch (error) {
    error.apiPath = path
    error.apiUrl = url
    error.apiMethod = 'DELETE'
    throw error
  }
}
